import { useEffect, useRef, useState } from 'react';
import {
  PRICING_TIERS, LAUNCH_PRICE_ENDS_AT, BUNDLES, isBundleRelevantForFeatures,
  type PlanType, type BundleId,
} from '../../shared/types';
import { useAuth } from '../contexts/AuthContext';
import { createCheckoutSession, createBundleCheckout, openBillingPortal } from '../services/api';

function formatPrice(cents: number | null): string {
  if (cents === null) return 'Sur devis';
  if (cents === 0) return '0€';
  return `${(cents / 100).toFixed(0)}€`;
}

function formatLaunchEndDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

interface PricingPageProps {
  onClose?: () => void;
  scrollTo?: string;
}

export default function PricingPage({ onClose, scrollTo }: PricingPageProps) {
  const bundlesRef = useRef<HTMLDivElement>(null);
  const { user, organization, features } = useAuth();

  useEffect(() => {
    if (scrollTo === 'bundles' && bundlesRef.current) {
      const timer = setTimeout(() => {
        bundlesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [scrollTo]);
  const [interval, setInterval] = useState<'month' | 'year'>('month');
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const currentPlan = organization?.plan ?? user?.plan ?? 'free';

  // Bundles pertinents pour le plan courant : on masque ceux dont TOUS les crédits
  // sont déjà couverts par l'abonnement (Premium → aucun ; Pro → social/événement/campagne ;
  // sans abo → tous). Évite de vendre des crédits redondants.
  const relevantBundles = BUNDLES
    .filter((b) => isBundleRelevantForFeatures(b, features))
    .sort((a, b) => a.priceCents - b.priceCents);
  const hasBundles = relevantBundles.length > 0;
  const hasActiveSub = !!organization?.stripeSubscriptionId;

  async function handleSubscribe(plan: PlanType) {
    if (plan === 'enterprise') return;
    if (!user) {
      setError('Vous devez être connecté pour souscrire à un abonnement.');
      return;
    }
    setError(null);
    setLoadingPlan(plan);
    try {
      const { url } = await createCheckoutSession(plan as 'pro' | 'premium', interval);
      if (!url) throw new Error('URL de paiement manquante.');
      window.location.href = url;
    } catch (err) {
      console.error('Checkout error:', err);
      setError(
        err instanceof Error
          ? `Impossible d'ouvrir la page de paiement : ${err.message}`
          : "Impossible d'ouvrir la page de paiement.",
      );
      setLoadingPlan(null);
    }
  }

  async function handleBundle(bundleId: BundleId) {
    if (!user) {
      setError('Vous devez être connecté pour acheter un bundle.');
      return;
    }
    setError(null);
    setLoadingPlan(`bundle:${bundleId}`);
    try {
      const { url } = await createBundleCheckout(bundleId);
      if (!url) throw new Error('URL de paiement manquante.');
      window.location.href = url;
    } catch (err) {
      console.error('Bundle checkout error:', err);
      setError(
        err instanceof Error
          ? `Impossible d'ouvrir la page de paiement : ${err.message}`
          : "Impossible d'ouvrir la page de paiement.",
      );
      setLoadingPlan(null);
    }
  }

  // Abonné existant : on route les changements d'offre vers le portail Stripe
  // (changement proraté, JAMAIS un 2e abonnement créé en parallèle).
  async function handlePortal() {
    setError(null);
    setLoadingPlan('portal');
    try {
      const { url } = await openBillingPortal();
      if (!url) throw new Error('URL du portail manquante.');
      window.location.href = url;
    } catch {
      setError("Impossible d'ouvrir le portail de facturation.");
      setLoadingPlan(null);
    }
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 animate-fade-in">
      {/* Back button */}
      {onClose && (
        <div className="mb-4">
          <button
            onClick={onClose}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-slate-600 dark:text-slate-400 shader:text-white/60 hover:bg-slate-100 dark:hover:bg-slate-800 shader:hover:bg-white/10 transition-colors"
            aria-label="Retour à l'accueil"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Retour
          </button>
        </div>
      )}

      {/* Header */}
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-full bg-amber-100 dark:bg-amber-900/30 shader:bg-amber-400/10 border border-amber-300 dark:border-amber-700 shader:border-amber-400/30">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
          <span className="text-xs font-medium text-amber-800 dark:text-amber-200 shader:text-amber-200">
            Prix de lancement Founding Member jusqu'au {formatLaunchEndDate(LAUNCH_PRICE_ENDS_AT)}
          </span>
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-normal mb-3 text-slate-900 dark:text-white shader:text-white">
          Choisissez votre abonnement
        </h1>
        <p className="text-slate-600 dark:text-slate-400 shader:text-white/60 max-w-xl mx-auto">
          Moins cher qu'une heure d'avocat. Plus rapide que n'importe quel cabinet.
        </p>
        {hasBundles && (
        <p className="mt-3 text-sm text-slate-500 dark:text-slate-400 shader:text-white/50 max-w-xl mx-auto">
          Besoin ponctuel ?{' '}
          <button
            type="button"
            onClick={() => bundlesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            className="underline text-indigo-600 dark:text-indigo-400 shader:text-indigo-300 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
          >
            Découvrez les bundles thématiques ci-dessous
          </button>
        </p>
        )}

        {/* Interval toggle */}
        <div className="flex items-center justify-center gap-3 mt-6">
          <button
            onClick={() => setInterval('month')}
            className={`px-4 py-1.5 text-sm rounded-full transition-colors ${
              interval === 'month'
                ? 'bg-indigo-600 text-white'
                : 'bg-slate-100 dark:bg-slate-800 shader:bg-white/10 text-slate-600 dark:text-slate-400 shader:text-white/60'
            }`}
          >
            Mensuel
          </button>
          <button
            onClick={() => setInterval('year')}
            className={`px-4 py-1.5 text-sm rounded-full transition-colors ${
              interval === 'year'
                ? 'bg-indigo-600 text-white'
                : 'bg-slate-100 dark:bg-slate-800 shader:bg-white/10 text-slate-600 dark:text-slate-400 shader:text-white/60'
            }`}
          >
            Annuel
            <span className="ml-1.5 text-xs text-emerald-500 font-medium">-20%</span>
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="max-w-2xl mx-auto mb-6 p-3 rounded-lg bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900 text-sm text-rose-700 dark:text-rose-300">
          {error}
        </div>
      )}

      {/* Pricing cards */}
      <div className="grid md:grid-cols-3 gap-5 mb-16 max-w-5xl mx-auto">
        {PRICING_TIERS.map((tier) => {
          const price = interval === 'year' ? tier.priceYearly : tier.priceMonthly;
          const regularPrice = interval === 'year' ? tier.priceYearlyRegular : tier.priceMonthlyRegular;
          const showLaunchPrice = tier.launchPrice === true && regularPrice != null && price != null && regularPrice > price;
          const isCurrentPlan = tier.plan === currentPlan;
          const isHighlighted = tier.highlighted;
          const isLoading = loadingPlan === tier.plan;

          return (
            <div
              key={tier.plan}
              className={`relative rounded-2xl p-6 flex flex-col transition-all ${
                isHighlighted
                  ? 'bg-indigo-600 text-white ring-2 ring-indigo-500 shadow-lg shadow-indigo-500/20 scale-[1.02]'
                  : 'bg-white dark:bg-slate-900 shader:bg-white/5 border border-slate-200 dark:border-slate-800 shader:border-white/10'
              }`}
            >
              {isHighlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-400 text-amber-900 text-xs font-bold px-3 py-1 rounded-full">
                  RECOMMANDÉ
                </div>
              )}

              <h3 className={`text-lg font-semibold mb-1 ${
                isHighlighted ? '' : 'text-slate-900 dark:text-white shader:text-white'
              }`}>
                {tier.name}
              </h3>
              <p className={`text-sm mb-4 ${
                isHighlighted ? 'text-indigo-200' : 'text-slate-500 dark:text-slate-400 shader:text-white/50'
              }`}>
                {tier.tagline}
              </p>

              <div className="mb-5">
                {showLaunchPrice && (
                  <div className={`inline-block text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded mb-1.5 ${
                    isHighlighted
                      ? 'bg-amber-400 text-amber-900'
                      : 'bg-amber-100 dark:bg-amber-900/40 shader:bg-amber-400/15 text-amber-800 dark:text-amber-200 shader:text-amber-200'
                  }`}>
                    Prix de lancement
                  </div>
                )}
                <div>
                  <span className={`text-3xl font-bold ${
                    isHighlighted ? '' : 'text-slate-900 dark:text-white shader:text-white'
                  }`}>
                    {price === null && tier.priceFromLabel ? tier.priceFromLabel : formatPrice(price)}
                  </span>
                  {((price !== null && price > 0) || tier.priceFromLabel) && (
                    <span className={`text-sm ${
                      isHighlighted ? 'text-indigo-200' : 'text-slate-500 dark:text-slate-400 shader:text-white/50'
                    }`}>
                      /mois
                    </span>
                  )}
                </div>
                {showLaunchPrice && regularPrice != null && (
                  // Wording carefully phrased forward-looking ("tarif normal après…")
                  // and NOT as a past reference price ("au lieu de…"). Art. L.112-1-1
                  // Code conso + Directive Omnibus : une annonce de réduction exige
                  // que le prix de référence ait été réellement pratiqué les 30
                  // jours précédents — ce qui n'est pas notre cas. Décrire une hausse
                  // future sort du périmètre de la règle.
                  <p className={`text-xs mt-1 ${
                    isHighlighted ? 'text-indigo-200' : 'text-slate-500 dark:text-slate-400 shader:text-white/50'
                  }`}>
                    Tarif normal après le {formatLaunchEndDate(LAUNCH_PRICE_ENDS_AT)} : {formatPrice(regularPrice)}/mois
                  </p>
                )}
                {showLaunchPrice && tier.launchLockMonths && (
                  <p className={`text-xs mt-0.5 font-medium ${
                    isHighlighted ? 'text-emerald-200' : 'text-emerald-600 dark:text-emerald-400 shader:text-emerald-300'
                  }`}>
                    Tarif verrouillé {tier.launchLockMonths} mois
                  </p>
                )}
              </div>

              {/* CTA button */}
              <button
                onClick={() => {
                  if (tier.plan === 'enterprise') {
                    window.location.href = 'mailto:contact@loievin.fr?subject=Plan%20Entreprise';
                  } else if (isCurrentPlan) {
                    // déjà désactivé
                  } else if (hasActiveSub) {
                    // Abonné : changement d'offre via le portail (proraté, pas de 2e abo).
                    handlePortal();
                  } else {
                    handleSubscribe(tier.plan);
                  }
                }}
                disabled={isCurrentPlan || isLoading}
                className={`w-full py-2.5 rounded-lg text-sm font-medium transition-colors mb-5 ${
                  isCurrentPlan
                    ? 'bg-slate-200 dark:bg-slate-700 shader:bg-white/10 text-slate-500 dark:text-slate-400 shader:text-white/40 cursor-default'
                    : isHighlighted
                      ? 'bg-white text-indigo-600 hover:bg-indigo-50'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700'
                } ${isLoading ? 'opacity-50 cursor-wait' : ''}`}
              >
                {isLoading ? 'Redirection...' : isCurrentPlan ? 'Plan actuel' : hasActiveSub ? "Gérer l'abonnement" : tier.cta}
              </button>

              {/* Features list */}
              <ul className="space-y-2 flex-1">
                {tier.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <svg className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                      isHighlighted ? 'text-emerald-300' : 'text-emerald-500'
                    }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className={isHighlighted ? 'text-indigo-100' : 'text-slate-600 dark:text-slate-400 shader:text-white/60'}>
                      {f}
                    </span>
                  </li>
                ))}
                {tier.disabledFeatures.map((f, i) => (
                  <li key={`d-${i}`} className="flex items-start gap-2 text-sm">
                    <svg className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                      isHighlighted ? 'text-indigo-300/50' : 'text-slate-300 dark:text-slate-600 shader:text-white/20'
                    }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span className={
                      isHighlighted ? 'text-indigo-300/50' : 'text-slate-400 dark:text-slate-600 shader:text-white/25'
                    }>
                      {f}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {/* Bundles thématiques — masqués si le plan couvre déjà tout (ex. Premium) */}
      {hasBundles && (
      <div ref={bundlesRef} className="mb-12">
        <h2 className="font-display text-2xl font-normal mb-2 text-center text-slate-900 dark:text-white shader:text-white">
          Bundles thématiques
        </h2>
        <p className="text-sm text-center text-slate-500 dark:text-slate-400 shader:text-white/50 mb-2 max-w-xl mx-auto">
          Pour un projet ponctuel, sans engagement. Les crédits du bundle restent
          utilisables sans limite de temps.
        </p>
        <p className="text-xs text-center text-slate-400 dark:text-slate-500 shader:text-white/40 mb-6 max-w-xl mx-auto">
          Un post réseau social = 1 crédit ; un carrousel en consomme plus selon sa taille
          (3 slides = 2 crédits, 7 slides = 4 crédits).
        </p>

        {/* Tous les bundles pertinents — une seule grille alignée (sans découpe Mono/Multi) */}
        <div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {relevantBundles.map((bundle) => {
              const isLoading = loadingPlan === `bundle:${bundle.id}`;
              return (
                <button
                  key={bundle.id}
                  onClick={() => handleBundle(bundle.id)}
                  disabled={loadingPlan !== null}
                  className={`flex flex-col p-5 rounded-xl border border-slate-200 dark:border-slate-700 shader:border-white/10 bg-white dark:bg-slate-900 shader:bg-white/5 hover:border-indigo-400 dark:hover:border-indigo-500 shader:hover:border-indigo-400/50 transition-colors text-left ${
                    isLoading ? 'cursor-wait opacity-70' : loadingPlan !== null ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <div className="flex items-baseline justify-between mb-2">
                    <h4 className="text-base font-semibold text-slate-900 dark:text-white shader:text-white">
                      {bundle.name}
                    </h4>
                    <span className="text-xl font-bold text-indigo-600 dark:text-indigo-400 shader:text-indigo-300">
                      {formatPrice(bundle.priceCents)}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 shader:text-white/50 mb-3">
                    {bundle.tagline}
                  </p>
                  <ul className="space-y-1.5 text-xs text-slate-600 dark:text-slate-400 shader:text-white/60 flex-1">
                    {bundle.creditLines.map((line, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <span className="text-indigo-500 mt-0.5">•</span>
                        <span>{line.label}</span>
                      </li>
                    ))}
                  </ul>
                  {isLoading && (
                    <p className="text-xs text-indigo-600 dark:text-indigo-400 shader:text-indigo-300 mt-3">
                      Redirection…
                    </p>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      )}

      {/* Value proposition footer */}
      <div className="text-center pb-4">
        <p className="text-xs text-slate-400 dark:text-slate-500 shader:text-white/30">
          Tous les prix sont en euros HT. Paiement sécurisé par Stripe.
          <br />
          Annulez à tout moment depuis votre espace de facturation.
          <br />
          Garantie satisfait ou remboursé 30 jours sur les abonnements, 7 jours sur les bundles.
        </p>
      </div>
    </div>
  );
}
